import React, { FC, ReactNode, useState } from "react";
import { css, styled } from "@mui/system";
import {
  CartesianGrid,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  DefaultLegendContentProps,
} from "recharts";
import { COLORS, FONTS, FONT_FAMILIES, SPACINGS, RADII, StatusColors, getColors } from "../../common/consts";
import { useTheme } from "../../common/contexts/ThemeContext";
import {
  EmailStatuses,
  ExtendedEmailStatuses,
  GeneralStatuses,
  OrganizationStats,
  OutreachEmailStatuses,
} from "../../types/organizations";
import { formatShortDate } from "../../common/utils/date-time";
import Button, { ButtonVariants } from "../../components/Display/Button";
import { TabSections } from "./ProspectsTabManager";
import ChartTooltip, {
  getColorByEntryName,
  getLabelByEntryName,
  getShapeByEntryName,
} from "./ChartTooltip";

/* The plot fills the card down to its bottom edge, the way Outreach draws a
   report chart, rather than floating with dead space underneath. */
const StyledContainer = styled("div")`
  width: 100%;
  height: 100%;
  min-width: 100px;
  display: flex;
  flex-direction: column;

  .recharts-responsive-container {
    flex: 1 1 auto;
  }
`;

const StyledList = styled("div")`
  justify-content: flex-end;
  align-items: center;
  background-color: var(--color-backgroundWhite, var(--color-backgroundPrimary, ${COLORS.backgroundPrimary}));
  padding: ${SPACINGS.sm} ${SPACINGS.md};
  border-radius: ${RADII.md} ${RADII.md} 0 0;
  display: flex;
  gap: ${SPACINGS.sm};
`;

export interface BaseMainChartEntry {
  date: string;
  percentages: OrganizationStats;
  prospectsChecked?: number;
}
interface MainChartDataEntry {
  date: string;
  prospectsChecked: number;
  [OutreachEmailStatuses.Valid]: number;
  [OutreachEmailStatuses.Invalid]: number;
  [OutreachEmailStatuses.Questionable]: number;
  [ExtendedEmailStatuses.EnrichedByBoring]: number;
  [ExtendedEmailStatuses.BouncesPrevented]: number;
}

export interface MainChartDataAccuracy extends BaseMainChartEntry {
  [OutreachEmailStatuses.Valid]: number;
  [OutreachEmailStatuses.Invalid]: number;
  [OutreachEmailStatuses.Questionable]: number;
}

export interface MainChartBoringRoiData extends BaseMainChartEntry {
  [OutreachEmailStatuses.Invalid]: number;
  [ExtendedEmailStatuses.EnrichedByBoring]?: number;
  [ExtendedEmailStatuses.BouncesPrevented]: number;
}

export type MainChartTabSpecificData = (
  | BaseMainChartEntry
  | MainChartDataAccuracy
  | MainChartBoringRoiData
)[];

export type MainChartData = MainChartDataEntry[];

interface Props {
  data: MainChartTabSpecificData;
  children?: ReactNode;
  selectedTab: TabSections;
  isEnrichmentEnabled?: boolean;
}

interface LegendButtonWrapperProps {
  color: string;
}

export const LegendButtonWrapper = styled("div")<LegendButtonWrapperProps>`
  ${({ color }) => css`
    color: ${color};
  `};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0;
`;

const StyledButton = styled(Button)`
  &&.MuiButton-root {
    padding: 0.7rem;
    font: ${FONTS.sm};
    font-weight: 300;
  }

  .MuiButton-startIcon {
    margin-right: 0.2rem;
  }
`;

const renderLegend = (
  props: Omit<DefaultLegendContentProps, "onClick"> & {
    onClick: (value: EmailStatuses) => void;
    visibleLines: Record<EmailStatuses, boolean>;
    selectedTab: TabSections;
    isEnrichmentEnabled?: boolean;
  }
) => {
  const { payload, onClick, selectedTab, visibleLines, isEnrichmentEnabled } = props;

  const getLegendsByTabs = (): Record<TabSections, EmailStatuses[]> => {
    const boringRoiLegends: EmailStatuses[] = [ExtendedEmailStatuses.BouncesPrevented];
    if (isEnrichmentEnabled) {
      boringRoiLegends.push(ExtendedEmailStatuses.EnrichedByBoring);
    }

    return {
      [TabSections.ProspectsVerified]: [GeneralStatuses.ProspectsChecked],
      [TabSections.BoringRoi]: boringRoiLegends,
      [TabSections.EmailDataAccuracy]: [
        OutreachEmailStatuses.Valid,
        OutreachEmailStatuses.Invalid,
        OutreachEmailStatuses.Questionable,
      ],
    };
  };

  const legendsByTabs = getLegendsByTabs();

  if (!payload) return;
  const legendsToRender = payload.filter((entry) =>
    legendsByTabs[selectedTab].includes(entry.value as EmailStatuses)
  );

  return (
    <StyledList className="w-100 d-flex">
      {legendsToRender?.map(({ value }, index) => {
        const ShapeComponent = getShapeByEntryName(value);
        const entryColor = getColorByEntryName(value);
        const status = value as EmailStatuses;

        return (
          <div key={`item-${index}`} onClick={() => onClick(status)}>
            <StyledButton
              key={`item-${index}`}
              variant={ButtonVariants.pill}
              active={visibleLines[status]}
              startIcon={
                <LegendButtonWrapper
                  color={
                    visibleLines[status]
                      ? COLORS.foregroundSecondaryLight
                      : entryColor
                  }
                >
                  <ShapeComponent />
                </LegendButtonWrapper>
              }
            >
              {getLabelByEntryName(value)}
            </StyledButton>
          </div>
        );
      })}
    </StyledList>
  );
};

const ANIMATION_THRESHOLD = 14; // Disable animations above this to prevent browser freezes

const MainChart: FC<Props> = ({ data, selectedTab, children, isEnrichmentEnabled = false }) => {
  const { mode } = useTheme();
  const colors = getColors(mode);
  const isAnimationActive = data.length <= ANIMATION_THRESHOLD;
  const [visibleLines, setVisibleLines] = useState<
    Record<EmailStatuses, boolean>
  >({
    prospectsChecked: false,
    [OutreachEmailStatuses.Valid]: false,
    [OutreachEmailStatuses.Invalid]: false,
    [OutreachEmailStatuses.Questionable]: false,
    [OutreachEmailStatuses.NoEmail]: false, // Keep for type compatibility, but not displayed
    [ExtendedEmailStatuses.EnrichedByBoring]: false,
    [ExtendedEmailStatuses.BouncesPrevented]: false,
  });

  const toggleLineVisibility = (key: EmailStatuses) => {
    setVisibleLines((prevState) => ({
      ...prevState,
      [key]: !prevState[key],
    }));
  };

  return (
    <StyledContainer>
      {children}
      <ResponsiveContainer width="100%" height="100%" minHeight={300}>
        <LineChart data={data} margin={{ top: 8, right: 24, bottom: 8, left: 8 }}>
          <CartesianGrid stroke="transparent" />
          <XAxis
            dataKey="date"
            tickLine={false}
            axisLine={{
              stroke: colors.chartAxisLine,
              fill: colors.chartAxisLine,
              color: colors.chartAxisLine,
            }}
            tick={{
              fill: colors.chartAxisColor,
              fontSize: "0.875rem",
              fontWeight: 400,
              fontFamily: FONT_FAMILIES.regular,
            }}
            tickFormatter={formatShortDate}
            padding={{ left: 20, right: 20 }}
          />
          <YAxis
            includeHidden
            width={52}
            tickLine={false}
            axisLine={{
              stroke: colors.chartAxisLine,
              fill: colors.chartAxisLine,
              color: colors.chartAxisLine,
            }}
            tick={{
              fill: colors.chartAxisColor,
              fontSize: "0.875rem",
              fontWeight: 400,
              fontFamily: FONT_FAMILIES.regular,
            }}
            padding={{ top: 5, bottom: 5 }}
          />
          <RechartsTooltip
            content={<ChartTooltip selectedTab={selectedTab} />}
          />
          <Legend
            verticalAlign="top"
            content={(props) =>
              renderLegend({
                ...props,
                onClick: toggleLineVisibility,
                visibleLines,
                selectedTab,
                isEnrichmentEnabled,
              })
            }
          />
          {selectedTab === TabSections.ProspectsVerified && (
            <Line
              type="linear"
              dataKey={GeneralStatuses.ProspectsChecked}
              stroke={StatusColors[GeneralStatuses.ProspectsChecked]}
              dot={false}
              isAnimationActive={isAnimationActive}
              strokeWidth={2}
              hide={visibleLines[GeneralStatuses.ProspectsChecked]}
            />
          )}
          <Line
            type="linear"
            dataKey={OutreachEmailStatuses.Valid}
            stroke={StatusColors[OutreachEmailStatuses.Valid]}
            dot={false}
            isAnimationActive={isAnimationActive}
            strokeWidth={2}
            hide={visibleLines[OutreachEmailStatuses.Valid]}
          />
          {selectedTab === TabSections.EmailDataAccuracy && (
            <Line
              type="linear"
              dataKey={OutreachEmailStatuses.Invalid}
              stroke={StatusColors[OutreachEmailStatuses.Invalid]}
              dot={false}
              isAnimationActive={isAnimationActive}
              strokeWidth={2}
              hide={visibleLines[OutreachEmailStatuses.Invalid]}
            />
          )}
          <Line
            type="linear"
            dataKey={OutreachEmailStatuses.Questionable}
            stroke={StatusColors[OutreachEmailStatuses.Questionable]}
            dot={false}
            isAnimationActive={isAnimationActive}
            strokeWidth={2}
            hide={visibleLines[OutreachEmailStatuses.Questionable]}
          />
          <Line
            type="linear"
            dataKey={ExtendedEmailStatuses.BouncesPrevented}
            stroke={StatusColors[ExtendedEmailStatuses.BouncesPrevented]}
            dot={false}
            isAnimationActive={isAnimationActive}
            strokeWidth={2}
            hide={visibleLines[ExtendedEmailStatuses.BouncesPrevented]}
          />
          {isEnrichmentEnabled && (
            <Line
              type="linear"
              dataKey={ExtendedEmailStatuses.EnrichedByBoring}
              stroke={StatusColors[ExtendedEmailStatuses.EnrichedByBoring]}
              dot={false}
              isAnimationActive={isAnimationActive}
              strokeWidth={2}
              hide={visibleLines[ExtendedEmailStatuses.EnrichedByBoring]}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </StyledContainer>
  );
};

export default MainChart;
